import copy from 'copy-to-clipboard';
import React, { useCallback } from 'react';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { useShowToast } from 'src/components/toast/Toast';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { usePerKeyLocalStorage } from 'src/hooks/usePerKeyLocalStorage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { getStudioLinkFromEmbed } from 'src/lib/routers/embedded';

import { GraphRef, graphRefToString } from '../../hooks/useGraphRef';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { createExplorerUrlState } from '../../routes';

import { convertRemoteStateToLocalState } from './useExplorerState/useEditorTabState/useSavedOperationLocalState';
import { INITIAL_HEADERS } from './useExplorerState/useHeadersManagerContext/shared';
import { headersStringFromArray } from './useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { useLoadSavedOperation } from './useLoadSavedOperation';

export interface SavedOperationState {
  type: 'saved';
  savedCollectionId: string;
  savedCollectionEntryId: string;
  sharedCollectionOriginVariantRef: GraphRef | undefined;
  isShared: boolean;
}

export type LocalOperationState = {
  type: 'local';
} & ResolvedOperation;

interface ResolvedOperation {
  document: string;
  variables: string;
  headers: string | undefined;
  preflightOperationScript: string;
  postflightOperationScript: string;
}

export type OperationState = LocalOperationState | SavedOperationState;

export type QueryParamOptions =
  | {
      [Config.queryParameters.ExplorerEndpoint]: string | undefined;
      [Config.queryParameters.SavedCollectionId]: string;
      [Config.queryParameters.SavedCollectionEntryId]: string;
      [Config.queryParameters.SharedCollectionOriginVariantRef]:
        | string
        | undefined;
      [Config.queryParameters.Referrer]: string;
    }
  | {
      [Config.queryParameters.ExplorerDocument]: string;
      [Config.queryParameters.ExplorerEndpoint]: string | undefined;
      [Config.queryParameters.ExplorerVariables]: string | undefined;
      [Config.queryParameters.ExplorerHeaders]: string | undefined;
      [Config.queryParameters.ExplorerPreflightOperationScript]:
        | string
        | undefined;
      [Config.queryParameters.ExplorerPostflightOperationScript]:
        | string
        | undefined;
    };

export function useCopyLinkToOperation() {
  const { pathToExplorer } = useInternalGraphLinking(false);
  const [sandboxUrl] = useLocalStorage('sandboxUrl');
  const [explorerLinkSettings] = useLocalStorage('explorerLinkSettings');
  const trackCustomEvent = useTrackCustomEvent();
  const isEmbedded = isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute();
  const [embedParentHref] = usePerKeyLocalStorage({
    initialValue: undefined,
    key: document.referrer,
    localStorageKey: 'embedParentHref',
  });

  const copyLinkWithQueryParams = useCallback(
    ({ queryParams }: { queryParams: QueryParamOptions }) => {
      // in the embed, we append the explorer specific query params to the
      // parent url, and parse them out. We want to keep all the existing
      // parent query params and just override ours. We don't want to append
      // the explorer path to the parent href, just the query params
      if (isEmbedded && embedParentHref) {
        const parentUrl = new URL(embedParentHref);
        if (
          parentUrl.searchParams.has(Config.queryParameters.ExplorerURLState)
        ) {
          parentUrl.searchParams.delete(
            Config.queryParameters.ExplorerURLState,
          );
        }
        const explorerURLState = createExplorerUrlState(queryParams);
        if (explorerURLState) {
          parentUrl.searchParams.append(
            Config.queryParameters.ExplorerURLState,
            explorerURLState,
          );
        }
        copy(parentUrl.href);
      } else {
        const baseHostname = isEmbedded
          ? getStudioLinkFromEmbed('')
          : Config.absoluteUrl.replace(/\/+$/, '');

        copy(
          `${baseHostname}${pathToExplorer({
            ...queryParams,
          })}`,
        );
      }
    },
    [embedParentHref, isEmbedded, pathToExplorer],
  );

  const loadSavedOperation = useLoadSavedOperation();
  const { showToasts } = useShowToast();

  return React.useCallback(
    async (operationState: OperationState): Promise<{ success: boolean }> => {
      if (operationState.type === 'saved' && operationState.isShared) {
        copyLinkWithQueryParams({
          queryParams: {
            endpoint: sandboxUrl ?? undefined,
            [Config.queryParameters.SavedCollectionId]:
              operationState.savedCollectionId,
            [Config.queryParameters.SavedCollectionEntryId]:
              operationState.savedCollectionEntryId,
            [Config.queryParameters.SharedCollectionOriginVariantRef]:
              operationState.sharedCollectionOriginVariantRef
                ? graphRefToString(
                    operationState.sharedCollectionOriginVariantRef,
                  )
                : undefined,
            [Config.queryParameters.Referrer]: 'operation_collections',
          },
        });
        return new Promise((resolve) => {
          resolve({ success: true });
        });
      } else if (operationState.type === 'saved') {
        const result = await loadSavedOperation({
          collectionId: operationState.savedCollectionId,
          collectionEntryId: operationState.savedCollectionEntryId,
          sharedCollectionOriginVariantRef: undefined, // if this is not a shared collection, there is no origin variant
        });
        const operationCollection = result.data?.operationCollection;
        if (operationCollection?.__typename !== 'OperationCollection') {
          showToasts({
            heading: 'Failed to copy saved operation',
            message: `Could not find operation collection${
              operationCollection ? `: ${operationCollection.message}` : ''
            }`,
            level: 'error',
          });
          return { success: false };
        }
        const operationEntry = operationCollection.operation;
        if (operationEntry?.__typename !== 'OperationCollectionEntry') {
          showToasts({
            heading: 'Failed to copy saved operation',
            message: `Could not find operation${
              operationEntry ? `: ${operationEntry.message}` : ''
            }`,
            level: 'error',
          });
          return { success: false };
        }
        const localOperationState = convertRemoteStateToLocalState(
          operationEntry.currentOperationRevision,
        );
        copyLinkWithQueryParams({
          queryParams: {
            document: localOperationState.operation ?? '',
            endpoint: sandboxUrl ?? undefined,
            variables: explorerLinkSettings.includeVariables
              ? localOperationState.variables
              : undefined,
            headers: explorerLinkSettings.includeHeaders
              ? headersStringFromArray(
                  localOperationState.headers ?? INITIAL_HEADERS,
                )
              : undefined,
            script: explorerLinkSettings.includePreflightOperationScript
              ? localOperationState.preflightOperationScript
              : undefined,
            postflightOperationScript:
              explorerLinkSettings.includePreflightOperationScript
                ? localOperationState.postflightOperationScript
                : undefined,
          },
        });
        return { success: true };
      } else {
        copyLinkWithQueryParams({
          queryParams: {
            document: operationState.document,
            endpoint: sandboxUrl ?? undefined,
            variables: explorerLinkSettings.includeVariables
              ? operationState.variables
              : undefined,
            headers: explorerLinkSettings.includeHeaders
              ? operationState.headers
              : undefined,
            script: explorerLinkSettings.includePreflightOperationScript
              ? operationState.preflightOperationScript
              : undefined,
            postflightOperationScript:
              explorerLinkSettings.includePostflightOperationScript
                ? operationState.postflightOperationScript
                : undefined,
          },
        });
      }

      trackCustomEvent({
        action: 'copy_link_to_document',
        category: 'Explorer',
      });
      return new Promise((resolve) => {
        resolve({ success: true });
      });
    },
    [
      trackCustomEvent,
      loadSavedOperation,
      copyLinkWithQueryParams,
      sandboxUrl,
      explorerLinkSettings.includeVariables,
      explorerLinkSettings.includeHeaders,
      explorerLinkSettings.includePreflightOperationScript,
      explorerLinkSettings.includePostflightOperationScript,
      showToasts,
    ],
  );
}
